import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { CREATE_jwel } from "../../Config/Config";

// Totals key -> the field name it's summed from on a raw API row.
// (Same mapping the original component's nine copy-pasted reduce()
// blocks used — written once here and reused everywhere: totals math,
// the data row, and the totals row all read off this single map.)
export const NUMERIC_FIELD_MAP = {
  QTY: "totqty",
  TOTAMT: "totamt",
  DISCOUNT: "discount",
  NETAMT: "NETAMT",
  CASH: "PAIDCASH",
  CARD: "PAIDCARD",
  UPI: "PAIDUPI",
  ONLINE: "PAIDONLINE",
  BALANCE: "BALANCE",
  CHEQUE: "PAIDCHEQUE",
};
export const NUMERIC_KEYS = Object.keys(NUMERIC_FIELD_MAP);

export const SOURCE_CONFIG = [
  { key: "SALE", label: "Sale" },
  { key: "SALE_RETURN", label: "Sale Return" },
  { key: "PURCHASE", label: "Purchase" },
  { key: "PURCHASE_RETURN", label: "Purchase Return" },
  { key: "SUPPLIER_PAYMENT", label: "Supplier Payment" },
  { key: "RECEIPT", label: "Receipt" },
  { key: "BANK_ENTRIES", label: "Bank Entries" },
  { key: "JOURNAL_RECEIPT", label: "Journal Receipt" },
  { key: "JOURNAL_PAYMENT", label: "Journal Payment" },
];

// ---------------------------------------------------------------------
// SINGLE SOURCE OF TRUTH for every Day Glance API this screen calls.
//
// All still-generic sources hit the same endpoint/shape:
//   GetDataFromGivenTableNameWithWhereandOrder?tableName=<X>
//     &where=BILLDATE>='<from>' AND BILLDATE<='<to>'&order=BILLNO
//
// To wire up a currently-empty tab, just fill in its table name below —
// nothing else in this file or in DayGlance.jsx needs to change.
//
// SALE and SALE_RETURN are `null` here because they don't fit this
// generic single-table shape — each has its own entry in
// SOURCE_FETCHERS below instead (joins an item-level table with
// SINGLE_ENTRY_DATA for payment breakdown).
// ---------------------------------------------------------------------
const SOURCE_TABLES = {
  SALE: null, // custom fetcher — see SOURCE_FETCHERS.SALE / fetchSaleRows below
  SALE_RETURN: null, // custom fetcher — see SOURCE_FETCHERS.SALE_RETURN / fetchSaleReturnRows below
  PURCHASE: null, // TODO: real table name
  PURCHASE_RETURN: null, // TODO: real table name
  SUPPLIER_PAYMENT: null, // TODO: real table name
  RECEIPT: null, // TODO: real table name
  BANK_ENTRIES: null, // TODO: real table name
  JOURNAL_RECEIPT: null, // TODO: real table name
  JOURNAL_PAYMENT: null, // TODO: real table name
};

// ---------------------------------------------------------------------
// Shared join logic for sources whose data lives across two unrelated
// tables and has to be joined in JS (currently SALE and SALE_RETURN):
//
//  1. An item-level table (BILL_ITEMS_DET for Sale, RETURN_DATA for
//     Sale Return) — several rows per billno. Grouped here into one
//     row per bill: Quantity = sum of qty, Total Amount = sum of
//     rate*qty (gross), Discount = sum of DisAmt, Net Amount = sum of
//     amount (already net — matches rate*qty - DisAmt in sample data).
//
//  2. SINGLE_ENTRY_DATA (where SALESTYPE=<source-specific value>) — a
//     double-entry ledger, NOT a flat payment-mode column. For a given
//     ENTRYNO (== billno) there's a pair of rows: one on the sales/
//     return side (LEDGERNAME like "CASH SALES"/"ONLINE SALES" — just
//     the debtor-side entry, not a money-movement row) and one on the
//     money-movement side, which is the one we actually want:
//       - STYPE === "CASH BOOK"                -> Cash
//       - STYPE === "BANK TRANS"                -> bucket from PAYMODE
//         (UPI / CARD / CHEQUE / NEFT-RTGS-IMPS-ONLINE)
//     The amount is whichever of JAMAAMOUNT/NAMAAMOUNT is populated
//     (only one side of the pair is ever nonzero).
// ---------------------------------------------------------------------

const PAYMODE_TO_BUCKET = {
  UPI: "UPI",
  CARD: "CARD",
  "DEBIT CARD": "CARD",
  "CREDIT CARD": "CARD",
  CHEQUE: "CHEQUE",
  CHQ: "CHEQUE",
  NEFT: "ONLINE",
  RTGS: "ONLINE",
  IMPS: "ONLINE",
  ONLINE: "ONLINE",
};

// Which money-movement bucket (if any) a SINGLE_ENTRY_DATA row belongs
// to. Rows that are just the sales/return-ledger counter-entry (STYPE
// like "CASH SALES"/"ONLINE SALES") return null and are skipped.
const resolveEntryBucket = (row) => {
  const stype = String(row.STYPE || "")
    .trim()
    .toUpperCase();
  if (stype === "CASH BOOK") return "CASH";
  if (stype === "BANK TRANS") {
    const mode = String(row.PAYMODE || "")
      .trim()
      .toUpperCase();
    return PAYMODE_TO_BUCKET[mode] || null;
  }
  return null;
};

const buildPaymentTotalsByEntryNo = (entryRows) =>
  entryRows.reduce((acc, row) => {
    const bucket = resolveEntryBucket(row);
    if (!bucket) return acc;

    const entryNo = row.ENTRYNO;
    if (entryNo === undefined || entryNo === null) return acc;

    if (!acc[entryNo])
      acc[entryNo] = { CASH: 0, CARD: 0, UPI: 0, ONLINE: 0, CHEQUE: 0 };
    // Only one of JAMAAMOUNT/NAMAAMOUNT is populated per row — sum picks
    // up whichever side actually has the value.
    acc[entryNo][bucket] += num(row.JAMAAMOUNT) + num(row.NAMAAMOUNT);
    return acc;
  }, {});

// Groups item-level rows (BILL_ITEMS_DET or RETURN_DATA — same field
// shape: billno / billdate / CustName / qty / rate / DisAmt / amount)
// into one row per billno.
const buildRowsFromItems = (itemRows) => {
  const byBillNo = new Map();

  itemRows.forEach((item) => {
    const billno = item.billno;
    if (billno === undefined || billno === null) return;

    if (!byBillNo.has(billno)) {
      byBillNo.set(billno, {
        billno,
        BillDate: item.billdate,
        CustName:
          item.CustName && item.CustName !== "-"
            ? item.CustName
            : item.PARTYNAME || "",
        totqty: 0,
        totamt: 0,
        discount: 0,
        NETAMT: 0,
      });
    }

    const row = byBillNo.get(billno);
    const qty = num(item.qty);
    row.totqty += qty;
    row.totamt += num(item.rate) * qty;
    row.discount += num(item.DisAmt);
    row.NETAMT += num(item.amount);
  });

  return Array.from(byBillNo.values());
};

// Generic two-table join fetcher used by both SALE and SALE_RETURN —
// fetches the item-level table and SINGLE_ENTRY_DATA in parallel, then
// merges them into one row per bill.
//   itemsTable:      table name for the item-level side
//   itemsExtraWhere: extra WHERE clause ANDed before the date range
//                     (e.g. "TYPE='B'" for Sale); pass null/undefined
//                     if the table needs no extra filter (Sale Return)
//   salesType:       the SINGLE_ENTRY_DATA.SALESTYPE value to match
const fetchJoinedRows = async (
  dates,
  tenantName,
  { itemsTable, itemsExtraWhere, salesType },
) => {
  const fromDate = dayjs(dates[0]).format("YYYY-MM-DD");
  const toDate = dayjs(dates[1]).format("YYYY-MM-DD");

  const dateClause = `CAST(billdate AS DATE) >= '${fromDate}' AND CAST(billdate AS DATE) <= '${toDate}'`;
  const itemsWhere = itemsExtraWhere
    ? `${itemsExtraWhere} AND ${dateClause}`
    : dateClause;
  const entriesWhere = `SALESTYPE='${salesType}' AND CAST(ENTRYDATE AS DATE) >= '${fromDate}' AND CAST(ENTRYDATE AS DATE) <= '${toDate}'`;

  const [itemsRes, entriesRes] = await Promise.all([
    axios.get(
      `${CREATE_jwel}/api/ERP/GetDataFromGivenTableNameWithWhere?tableName=${itemsTable}&where=${itemsWhere}`,
      { headers: { tenantName } },
    ),
    axios.get(
      `${CREATE_jwel}/api/ERP/GetDataFromGivenTableNameWithWhere?tableName=SINGLE_ENTRY_DATA&where=${entriesWhere}`,
      { headers: { tenantName } },
    ),
  ]);

  const rows = buildRowsFromItems(itemsRes.data || []);
  const paymentTotalsByEntryNo = buildPaymentTotalsByEntryNo(
    entriesRes.data || [],
  );

  return rows.map((row, index) => {
    const payments = paymentTotalsByEntryNo[row.billno] || {};
    return {
      ...row,
      PAIDCASH: payments.CASH || 0,
      PAIDCARD: payments.CARD || 0,
      PAIDUPI: payments.UPI || 0,
      PAIDONLINE: payments.ONLINE || 0,
      PAIDCHEQUE: payments.CHEQUE || 0,
      BALANCE: 0, // not derivable from these two tables
      key: index + 1,
      serialNo: index + 1,
    };
  });
};

const fetchSaleRows = (dates, tenantName) =>
  fetchJoinedRows(dates, tenantName, {
    itemsTable: "BILL_ITEMS_DET",
    itemsExtraWhere: "TYPE='B'",
    salesType: "BILLING",
  });

const fetchSaleReturnRows = (dates, tenantName) =>
  fetchJoinedRows(dates, tenantName, {
    itemsTable: "RETURN_DATA",
    itemsExtraWhere: null,
    salesType: "SALE RETURN",
  });

// Escape hatch: if a source turns out NOT to fit the generic
// tableName/BILLDATE/BILLNO shape above (different endpoint, needs to
// merge two tables, needs extra filtering, etc.), give it its own entry
// here instead of a SOURCE_TABLES row. It takes priority over
// SOURCE_TABLES when present.
const SOURCE_FETCHERS = {
  SALE: fetchSaleRows,
  SALE_RETURN: fetchSaleReturnRows,
};

const emptyTotals = () =>
  NUMERIC_KEYS.reduce((acc, key) => {
    acc[key] = 0;
    return acc;
  }, {});

const num = (v) => Number(v) || 0;

const addIntoTotals = (totals, row) => {
  NUMERIC_KEYS.forEach((key) => {
    totals[key] += num(row[NUMERIC_FIELD_MAP[key]]);
  });
  return totals;
};

// Generic fetcher shared by every SOURCE_TABLES entry — identical
// endpoint, identical BILLDATE/BILLNO query shape, just a different
// table name plugged in.
const fetchByTableName = async (tableName, dates, tenantName) => {
  const fromDate = dayjs(dates[0]).format("YYYY-MM-DD");
  const toDate = dayjs(dates[1]).format("YYYY-MM-DD");

  const { data } = await axios.get(
    `${CREATE_jwel}/api/ERP/GetDataFromGivenTableNameWithWhereandOrder?tableName=${tableName}&where=BILLDATE>='${fromDate}' AND BILLDATE<='${toDate}'&order=BILLNO`,
    { headers: { tenantName } },
  );

  return (data || []).map((item, index) => ({
    ...item,
    key: index + 1,
    serialNo: index + 1,
  }));
};

const fetchSourceRows = async (key, dates, tenantName) => {
  if (SOURCE_FETCHERS[key]) {
    return SOURCE_FETCHERS[key](dates, tenantName);
  }
  const tableName = SOURCE_TABLES[key];
  if (!tableName) return []; // not wired up yet — see SOURCE_TABLES TODOs
  return fetchByTableName(tableName, dates, tenantName);
};

const useDayGlance = (dates) => {
  const [sourceRows, setSourceRows] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const tenantName =
    typeof window !== "undefined" ? localStorage.getItem("tenantName") : null;

  const fetchAll = useCallback(async () => {
    if (!dates[0] || !dates[1]) return;

    setLoading(true);
    setError(null);

    try {
      const entries = await Promise.all(
        SOURCE_CONFIG.map(async ({ key }) => {
          try {
            const rows = await fetchSourceRows(key, dates, tenantName);
            return [key, rows];
          } catch (err) {
            console.error(`Error fetching ${key}:`, err);
            return [key, []];
          }
        }),
      );
      setSourceRows(Object.fromEntries(entries));
    } catch (err) {
      console.error("Error fetching Day Glance data:", err);
      setError(err);
      setSourceRows({});
    } finally {
      setLoading(false);
    }
  }, [dates, tenantName]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const tabsData = useMemo(() => {
    const result = {};
    SOURCE_CONFIG.forEach(({ key }) => {
      const rows = sourceRows[key] || [];
      result[key] = { rows, totals: rows.reduce(addIntoTotals, emptyTotals()) };
    });
    return result;
  }, [sourceRows]);

  const allRows = useMemo(
    () => SOURCE_CONFIG.flatMap(({ key }) => tabsData[key]?.rows || []),
    [tabsData],
  );

  const allTotals = useMemo(
    () => allRows.reduce(addIntoTotals, emptyTotals()),
    [allRows],
  );

  return { loading, error, tabsData, allRows, allTotals, refetch: fetchAll };
};

export default useDayGlance;
